/**
 * Region default for analytics consent (flag `analytics-region-default`).
 *
 * Outside the opt-in jurisdictions, a browser that has made no cookie choice
 * gets analytics on by default. The flag's only condition is geographic
 * (`$geoip_country_code` set and not in the opt-in list, which lives in the
 * PostHog condition and in `docs/compliance/cookie-consent.md`), so its answer
 * arrives with every /flags response, and this module reconciles the stored
 * consent against it each time.
 *
 * Kept out of `analytics.ts` on purpose: it needs only three of the wrapper's
 * controls, passed in, which keeps the wrapper under its size limit and leaves
 * no import cycle (`emailCtaCapture` already imports the wrapper).
 */
import posthog from 'posthog-js';
import { FEATURE_FLAGS } from '@bower/shared/constants/featureFlags';
import { ANALYTICS_EVENTS } from '../constants/analytics';
import type { BaseEventProperties } from '../types/analytics';
import {
  applyRegionDefaultConsent,
  clearRegionDefaultConsent,
  getConsentState,
  isRegionDefaultConsent,
} from './cookieConsent';

/** The parts of the analytics wrapper a region-default change drives. */
export interface RegionDefaultControls {
  enableAnalyticsAfterConsent(): void;
  disableAnalyticsAfterRevocation(): void;
  track(eventName: string, eventProperties?: BaseEventProperties): void;
}

/**
 * Apply or withdraw the region default for one /flags answer.
 *
 * - An explicit choice is never touched, in either direction.
 * - No choice + flag TRUE: store the region default and upgrade to full
 *   analytics, exactly as the banner's "Accept all" does.
 * - Region default stored + flag FALSE (the browser is now in an opt-in
 *   jurisdiction, or the flag was turned off): withdraw it and downgrade, so
 *   the banner shows again. A flag missing from a successful response (a
 *   disabled flag) counts as false.
 * - A /flags error is "unknown", not "false", so it changes nothing. Note the
 *   window this leaves: a region-default browser initialises with full capture
 *   from its stored state, and is downgraded only when /flags answers.
 *
 * Ordering on apply: store, then upgrade capture, then notify the banner. A
 * write that throws leaves capture off (nothing could switch it off again, since
 * the banner and Settings write to the same blocked storage); and the banner
 * replays stashed payloads the moment it is notified, which a flags-only
 * wrapper would silently drop.
 *
 * The stored blob is shared across tabs, so another tab can clear this tab's
 * default. `heldInThisPage` remembers that THIS page took or loaded a region
 * default, so a false flag still withdraws it here when the blob is already
 * gone. Nothing else is downgraded on a missing blob: a tab that never took the
 * default (including one where the person clicked Accept) is left alone.
 *
 * Never throws: this runs inside posthog-js's flag-callback loop, which has no
 * guard, so a throw (localStorage blocked or full) would starve every flag
 * subscriber registered after it.
 */
let heldInThisPage = false;

export function reconcileRegionDefault(
  controls: RegionDefaultControls,
  errorsLoading: boolean,
): void {
  if (errorsLoading) return;
  try {
    const state = getConsentState();
    if (state && state.basis !== 'region_default') return;
    if (state) heldInThisPage = true;

    const defaultOn = posthog.isFeatureEnabled(FEATURE_FLAGS.ANALYTICS_REGION_DEFAULT) === true;
    if (defaultOn && !state) {
      applyRegionDefaultConsent(() => controls.enableAnalyticsAfterConsent());
      heldInThisPage = true;
      controls.track(ANALYTICS_EVENTS.COOKIE_CONSENT_DEFAULTED, {
        analytics: true,
        functional: true,
      });
    } else if (!defaultOn && heldInThisPage) {
      // Deferred past the callback loop: the downgrade calls posthog.reset(),
      // which would wipe the flags the remaining subscribers are about to read.
      queueMicrotask(() => withdrawRegionDefault(controls));
    }
  } catch (error) {
    console.warn('Could not reconcile the analytics region default:', error);
  }
}

function withdrawRegionDefault(controls: RegionDefaultControls): void {
  try {
    // Cleared here, or already cleared by another tab: either way only a
    // browser with no explicit choice is downgraded.
    if (!clearRegionDefaultConsent() && getConsentState() !== null) return;
    heldInThisPage = false;
    controls.disableAnalyticsAfterRevocation();
  } catch (error) {
    console.warn('Could not withdraw the analytics region default:', error);
  }
}

/**
 * Where the region default runs: production, and builds labelled `staging`,
 * which are the staging deployment AND Firebase preview channels
 * (`preview-deploy.yml` builds with `VITE_APP_ENV: staging`). That is so the
 * staging-only flag can be verified. Neither sends events: the production host
 * gate in `analytics.ts` drops them, exactly as after "Accept all" there today;
 * what they show is the banner, the stored basis and Settings. Local dev never
 * takes a region default.
 */
export function regionDefaultRunsIn(appEnv: string): boolean {
  return appEnv === 'production' || appEnv === 'staging';
}

/** Reconcile on every /flags response for the life of the page. */
export function subscribeRegionConsentDefault(
  controls: RegionDefaultControls,
  appEnv: string,
): void {
  if (!regionDefaultRunsIn(appEnv)) return;
  // init() has already turned capture on from a stored region default, so the
  // page holds it from here, even if another tab clears the blob before this
  // page's first /flags answer.
  heldInThisPage = isRegionDefaultConsent();
  posthog.onFeatureFlags((_flags, _variants, context) => {
    reconcileRegionDefault(controls, context?.errorsLoading === true);
  });
}
